import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button, DialogFooter } from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import { getEpisode, updateEpisode } from '../../services/api';
import type {
  EpisodeResponse,
  EpisodeUpdate,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  type EpisodeEditImpactWarnings,
  parseEpisodeEditImpactWarnings,
} from '../../utils/episodeEditImpact';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { MAX_EPISODE_END_DATE_OFFSET } from '../../utils/episodeBoundaryVisits';
import { DateField } from '../FormFields';
import { EpisodeEditImpactList } from './EpisodeEditImpactList';
import PreClaimReviewCard from './PreClaimReviewCard';

export type EpisodeEditorProps = {
  episodeId: string;
  onCancel?: () => void;
  onSaved?: () => void;
};

const PCR_LENGTH = 14;

const isPcrValid = (value: string): boolean =>
  value.length === 0 || value.length === PCR_LENGTH;

// Statuses whose dates + active/inactive toggle are editable from the pencil.
const isCoreEditableStatus = (status: string): boolean =>
  status === 'active' || status === 'cancelled';

const statusDisplayLabel = (status: string): string =>
  status === 'cancelled' ? 'Inactive' : status === 'active' ? 'Active' : status;

// Whole days for an ISO "YYYY-MM-DD" string, computed in UTC so it is timezone-safe.
const isoDayNumber = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
};

const episodeToForm = (episode: EpisodeResponse) => ({
  pcrNumber1: episode.pcr_number_1 ?? '',
  pcrNumber2: episode.pcr_number_2 ?? '',
  pcrComment: episode.pcr_comment ?? '',
  startDate: episode.start_date,
  endDate: episode.end_date,
  status: episode.status,
});

const EpisodeEditor: React.FC<EpisodeEditorProps> = ({
  episodeId,
  onCancel,
  onSaved,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [episode, setEpisode] = useState<EpisodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pcrNumber1, setPcrNumber1] = useState('');
  const [pcrNumber2, setPcrNumber2] = useState('');
  const [pcrComment, setPcrComment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [pendingImpact, setPendingImpact] =
    useState<EpisodeEditImpactWarnings | null>(null);

  const usingRcd = episode?.clinic?.using_rcd ?? false;
  const coreEditable = episode ? isCoreEditableStatus(episode.status) : false;

  const resetForm = useCallback((data: EpisodeResponse) => {
    const form = episodeToForm(data);
    setPcrNumber1(form.pcrNumber1);
    setPcrNumber2(form.pcrNumber2);
    setPcrComment(form.pcrComment);
    setStartDate(form.startDate);
    setEndDate(form.endDate);
    setStatusDraft(form.status);
  }, []);

  const fetchEpisode = useCallback(async () => {
    setLoading(true);
    // Drop any stale impact confirmation from a previously loaded episode.
    setPendingImpact(null);
    try {
      const response = await getEpisode({
        path: { episode_id: episodeId },
      });
      const data = response.data ?? null;
      setEpisode(data);
      if (data) {
        resetForm(data);
      }
    } catch (error) {
      console.error('Failed to load episode:', error);
      showError(
        'Load Failed',
        'Unable to load episode data. Please try again.'
      );
      setEpisode(null);
    } finally {
      setLoading(false);
    }
  }, [episodeId, resetForm, showError]);

  useEffect(() => {
    void fetchEpisode();
  }, [fetchEpisode]);

  // Inline date error mirroring the backend rules: end must be after start, and the
  // range may not be lengthened beyond 60 days (a legacy range already over 60 days
  // may shrink or shift without tripping the cap).
  const dateError = useMemo(() => {
    if (!coreEditable || !episode) {
      return null;
    }
    if (endDate <= startDate) {
      return 'End date must be after start date.';
    }
    const newLength = isoDayNumber(endDate) - isoDayNumber(startDate);
    const oldLength =
      isoDayNumber(episode.end_date) - isoDayNumber(episode.start_date);
    if (newLength > MAX_EPISODE_END_DATE_OFFSET && newLength > oldLength) {
      return 'Episode cannot be longer than 60 days.';
    }
    return null;
  }, [coreEditable, episode, endDate, startDate]);

  const datesValid = dateError === null;

  const hasChanges = useMemo(() => {
    if (!episode) {
      return false;
    }
    const coreChanged =
      coreEditable &&
      (startDate !== episode.start_date ||
        endDate !== episode.end_date ||
        statusDraft !== episode.status);
    const pcrChanged =
      usingRcd &&
      ((episode.pcr_number_1 ?? '') !== pcrNumber1 ||
        (episode.pcr_number_2 ?? '') !== pcrNumber2 ||
        (episode.pcr_comment ?? '') !== pcrComment.trim());
    return coreChanged || pcrChanged;
  }, [
    episode,
    coreEditable,
    startDate,
    endDate,
    statusDraft,
    usingRcd,
    pcrNumber1,
    pcrNumber2,
    pcrComment,
  ]);

  const saveDisabled = useMemo(
    () =>
      saving ||
      !hasChanges ||
      !datesValid ||
      !isPcrValid(pcrNumber1) ||
      !isPcrValid(pcrNumber2),
    [saving, hasChanges, datesValid, pcrNumber1, pcrNumber2]
  );

  const buildBody = useCallback((): EpisodeUpdate => {
    if (!episode) {
      return {};
    }
    const body: EpisodeUpdate = {};
    if (usingRcd) {
      // Only send PCR fields that actually changed. Sending unchanged PCR fields
      // would trip the backend RCD-eligibility check for a dates/status-only edit
      // if the clinic's RCD flag changed after the form loaded.
      const p1 = pcrNumber1.length > 0 ? pcrNumber1 : null;
      const p2 = pcrNumber2.length > 0 ? pcrNumber2 : null;
      const comment = pcrComment.trim().length > 0 ? pcrComment.trim() : null;
      if (p1 !== (episode.pcr_number_1 ?? null)) {
        body.pcr_number_1 = p1;
      }
      if (p2 !== (episode.pcr_number_2 ?? null)) {
        body.pcr_number_2 = p2;
      }
      if (comment !== (episode.pcr_comment ?? null)) {
        body.pcr_comment = comment;
      }
    }
    if (coreEditable) {
      if (startDate !== episode.start_date) {
        body.start_date = startDate;
      }
      if (endDate !== episode.end_date) {
        body.end_date = endDate;
      }
      if (statusDraft !== episode.status) {
        body.status = statusDraft;
      }
    }
    return body;
  }, [
    episode,
    usingRcd,
    pcrNumber1,
    pcrNumber2,
    pcrComment,
    coreEditable,
    startDate,
    endDate,
    statusDraft,
  ]);

  const submit = useCallback(
    async (acknowledgeWarnings: boolean) => {
      setSaving(true);
      try {
        const response = await updateEpisode({
          path: { episode_id: episodeId },
          body: buildBody(),
          query: acknowledgeWarnings
            ? { acknowledge_warnings: true }
            : undefined,
          // The generated client resolves (does not throw) on a 4xx by default, so
          // opt into throwing to route 409 impact / 400 block responses through the
          // catch below instead of being mistaken for a successful save.
          throwOnError: true,
        });
        if (response.data) {
          setEpisode(response.data);
          resetForm(response.data);
        }
        setPendingImpact(null);
        showSuccess('Episode Saved', 'Episode information was updated.');
        onSaved?.();
      } catch (error) {
        const impact = parseEpisodeEditImpactWarnings(error);
        if (impact) {
          setPendingImpact(impact);
          return;
        }
        console.error('Failed to save episode:', error);
        // Leave the confirmation screen so the error toast is the only prompt; the
        // user can re-open the editor to retry.
        setPendingImpact(null);
        showError(
          'Save Failed',
          getBusinessErrorMessage(error) ??
            'Unable to save episode information. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    },
    [episodeId, buildBody, resetForm, showSuccess, showError, onSaved]
  );

  const handleCancel = () => {
    if (episode) {
      resetForm(episode);
    }
    onCancel?.();
  };

  const toggleStatus = () => {
    setStatusDraft(prev => (prev === 'active' ? 'cancelled' : 'active'));
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (!episode) {
    return (
      <p className='text-sm text-muted-foreground'>
        Episode data is not available.
      </p>
    );
  }

  if (pendingImpact) {
    return (
      <>
        <div className='space-y-3' data-testid='episode-edit-impact'>
          <p className='text-sm font-medium'>
            This edit affects existing records
          </p>
          <EpisodeEditImpactList warnings={pendingImpact} />
          <p className='text-sm text-muted-foreground'>
            These records are not modified. Do you want to proceed?
          </p>
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => setPendingImpact(null)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void submit(true)}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <div className='space-y-4'>
        {episode.clinic && (
          <div className='rounded-md border bg-muted/30 px-3 py-2 text-sm'>
            <p className='font-medium'>{episode.clinic.name}</p>
            {episode.clinic.clinic_key && (
              <p className='text-muted-foreground'>
                Clinic key: {episode.clinic.clinic_key}
              </p>
            )}
            {(episode.clinic.npi || episode.clinic.phone) && (
              <p className='text-muted-foreground'>
                {[
                  episode.clinic.npi && `NPI ${episode.clinic.npi}`,
                  episode.clinic.phone,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
        )}

        <div className='space-y-3 rounded-md border px-3 py-3'>
          <p className='text-sm font-medium'>Episode</p>
          {coreEditable ? (
            <>
              <DateField
                label='Start date'
                htmlFor='episode-start-date'
                data-testid='episode-start-date'
                value={startDate}
                onChange={(value: string | null) => {
                  if (value) setStartDate(value);
                }}
                fieldSize='default'
              />
              <DateField
                label='End date'
                htmlFor='episode-end-date'
                data-testid='episode-end-date'
                value={endDate}
                onChange={(value: string | null) => {
                  if (value) setEndDate(value);
                }}
                fieldSize='default'
              />
              {dateError && (
                <p className='text-sm text-destructive'>{dateError}</p>
              )}
            </>
          ) : (
            <div className='text-sm text-muted-foreground'>
              <p>Start date: {formatDateForDisplay(episode.start_date)}</p>
              <p>End date: {formatDateForDisplay(episode.end_date)}</p>
            </div>
          )}

          <div className='flex items-center justify-between gap-3'>
            <span className='text-sm'>
              Status:{' '}
              <span data-testid='episode-status-value' className='font-medium'>
                {statusDisplayLabel(
                  coreEditable ? statusDraft : episode.status
                )}
              </span>
            </span>
            {coreEditable && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                data-testid='episode-status-toggle'
                onClick={toggleStatus}
                disabled={saving}
              >
                {statusDraft === 'active'
                  ? 'Deactivate episode'
                  : 'Reactivate episode'}
              </Button>
            )}
          </div>
        </div>

        {usingRcd ? (
          <PreClaimReviewCard
            pcrNumber1={pcrNumber1}
            pcrNumber2={pcrNumber2}
            pcrComment={pcrComment}
            saving={saving}
            onPcrNumber1Change={setPcrNumber1}
            onPcrNumber2Change={setPcrNumber2}
            onPcrCommentChange={setPcrComment}
          />
        ) : (
          <p className='text-sm text-muted-foreground'>
            Pre-Claim Review is not enabled for this clinic.
          </p>
        )}
      </div>
      <DialogFooter>
        <Button
          type='button'
          variant='outline'
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type='button'
          onClick={() => void submit(false)}
          disabled={saveDisabled}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </>
  );
};

export default EpisodeEditor;
